import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { WebAuthProvider, useWebAuth } from "@/lib/webAuth";
import ErrorBoundary from "@/components/ErrorBoundary";
import InAppBrowserBanner from "@/components/InAppBrowserBanner";
// Admin pages
import LoginPage from "@/pages/LoginPage";
import AdminLayout from "@/layouts/AdminLayout";
import Dashboard from "@/pages/Dashboard";
import Users from "@/pages/Users";
import PendingPayments from "@/pages/PendingPayments";
import Transactions from "@/pages/Transactions";
import Packages from "@/pages/Packages";
import Settings from "@/pages/Settings";
import Conversations from "@/pages/Conversations";
import URLScans from "@/pages/URLScans";
import CreditAudit from "@/pages/CreditAudit";
import Broadcast from "@/pages/Broadcast";
import FraudEvents from "@/pages/FraudEvents";
import Activity from "@/pages/Activity";
import OSINTUsage from "@/pages/OSINTUsage";
// Web pages
import Landing from "@/pages/web/Landing";
import WebChat from "@/pages/web/WebChat";
import WebBuy from "@/pages/web/WebBuy";
import WebOrders from "@/pages/web/WebOrders";
import WebLogin from "@/pages/web/WebLogin";
import WebRegister from "@/pages/web/WebRegister";

import { Toaster } from "@/components/ui/sonner";

function RequireAdmin({ children }) {
  const { admin, loading } = useAuth();
  if (loading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-zinc-950 text-zinc-500 text-sm">
        Cargando…
      </div>
    );
  }
  if (!admin) return <Navigate to="/admin/login" replace />;
  return children;
}

// Block web routes until user is authenticated (email/password or legacy code)
function RequireWebUser({ children }) {
  const { user, loading } = useWebAuth();
  if (loading) {
    return (
      <div className="min-h-[100dvh] flex items-center justify-center bg-black text-[#00E5FF] text-sm font-cyber tracking-widest">
        ⚡ INICIANDO IARISK...
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

export default function App() {
  return (
    <ErrorBoundary>
      <div className="App">
        <InAppBrowserBanner />
        <BrowserRouter>
          <AuthProvider>
            <WebAuthProvider>
              <Routes>
              {/* Public web app */}
              <Route path="/" element={<Landing />} />
              <Route path="/login" element={<WebLogin />} />
              <Route path="/registro" element={<WebRegister />} />
              <Route path="/chat" element={<RequireWebUser><WebChat /></RequireWebUser>} />
              <Route path="/comprar" element={<RequireWebUser><WebBuy /></RequireWebUser>} />
              <Route path="/ordenes" element={<RequireWebUser><WebOrders /></RequireWebUser>} />

              {/* Admin panel (kept exactly as before, just under /admin) */}
              <Route path="/admin/login" element={<LoginPage />} />
              <Route
                path="/admin"
                element={
                  <RequireAdmin>
                    <AdminLayout />
                  </RequireAdmin>
                }
              >
                <Route index element={<Dashboard />} />
                <Route path="pagos" element={<PendingPayments />} />
                <Route path="usuarios" element={<Users />} />
                <Route path="transacciones" element={<Transactions />} />
                <Route path="paquetes" element={<Packages />} />
                <Route path="conversaciones" element={<Conversations />} />
                <Route path="urls" element={<URLScans />} />
                <Route path="creditos" element={<CreditAudit />} />
                <Route path="anuncios" element={<Broadcast />} />
                <Route path="fraude" element={<FraudEvents />} />
                <Route path="actividad" element={<Activity />} />
                <Route path="osint" element={<OSINTUsage />} />
                <Route path="ajustes" element={<Settings />} />
              </Route>
              {/* Backward compat for old admin URLs (when admin panel was at root) */}
              <Route path="/usuarios" element={<Navigate to="/admin/usuarios" replace />} />
              <Route path="/pagos" element={<Navigate to="/admin/pagos" replace />} />
              <Route path="/transacciones" element={<Navigate to="/admin/transacciones" replace />} />
              <Route path="/paquetes" element={<Navigate to="/admin/paquetes" replace />} />
              <Route path="/conversaciones" element={<Navigate to="/admin/conversaciones" replace />} />
              <Route path="/urls" element={<Navigate to="/admin/urls" replace />} />
              <Route path="/creditos" element={<Navigate to="/admin/creditos" replace />} />
              <Route path="/anuncios" element={<Navigate to="/admin/anuncios" replace />} />
              <Route path="/fraude" element={<Navigate to="/admin/fraude" replace />} />
              <Route path="/ajustes" element={<Navigate to="/admin/ajustes" replace />} />
              <Route path="/dashboard" element={<Navigate to="/admin" replace />} />
              <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
              <Toaster richColors position="top-right" />
            </WebAuthProvider>
          </AuthProvider>
        </BrowserRouter>
      </div>
    </ErrorBoundary>
  );
}
